import { AddressComponents } from "components/AddressComponent/AddressComponent";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Input from "components/DesignSystem/Input/Input";
import { SENSITIVE_DATA_MANUAL_BANK_KEY } from "constants/sensitiveData";
import { getIn, useFormikContext } from "formik";
import { AddressAutofill } from "types/Models/addressAutofill";
import { ManualBankAccountValues } from "types/Models/manualBankAccount";

type ManualBankAccountFieldsProps = {
  entityId: string;
  groupId: string;
};

const fieldName = (key: keyof ManualBankAccountValues) =>
  `${SENSITIVE_DATA_MANUAL_BANK_KEY}.${key}`;

export const ManualBankAccountFields = ({
  entityId,
  groupId,
}: ManualBankAccountFieldsProps) => {
  const { values, errors, submitCount, setFieldValue } =
    useFormikContext<Record<string, unknown>>();

  const manualBank = getIn(values, SENSITIVE_DATA_MANUAL_BANK_KEY) as
    | ManualBankAccountValues
    | undefined;

  const addressError = getIn(errors, fieldName("address"));
  const isSubmitted = submitCount > 0;

  const handleAddressSelect = (address: AddressAutofill | null) => {
    setFieldValue(fieldName("address"), address?.uuid || "");
  };

  const handleConfirmChange = () => {
    setFieldValue(fieldName("is_confirmed"), !manualBank?.is_confirmed);
  };

  return (
    <div className="t-flex t-flex-col t-gap-3">
      <div className="t-grid t-grid-cols-2 t-gap-3 sm:t-grid-cols-1">
        <Input
          withForm
          name={fieldName("name")}
          showErrorOnceTouched
          label="Bank name"
          placeholder="Enter bank name"
          required
        />
        <Input
          withForm
          name={fieldName("account_number")}
          showErrorOnceTouched
          label="Account number"
          placeholder="Enter account number"
          required
        />
        <Input
          withForm
          name={fieldName("routing_number")}
          showErrorOnceTouched
          label="Routing number"
          placeholder="Enter routing number"
          required
        />
        <Input
          withForm
          name={fieldName("bank_details_entity_name")}
          showErrorOnceTouched
          label="Account holder's name"
          placeholder="Enter account holder's name"
          required
        />
      </div>

      <AddressComponents
        formPrefix=""
        entityId={entityId}
        groupId={groupId}
        selectedAddressId={manualBank?.address || ""}
        label="Bank account holder's address"
        size="regular"
        onAddressSelect={handleAddressSelect}
        error={isSubmitted ? (addressError as string) : undefined}
        hideClear
        srollToSave
      />

      <Checkbox
        alignTop
        className="[&>input]:t-mt-1"
        name={fieldName("is_confirmed")}
        checked={Boolean(manualBank?.is_confirmed)}
        onChange={handleConfirmChange}
        label={
          <span className="t-text-body-sm t-text-text-60">
            I confirm that the information provided is accurate and that this
            bank account is active as of the date of verification.
          </span>
        }
      />
    </div>
  );
};
